class GiftWrapping extends HTMLElement {
  constructor() {
    super();

    this.giftWrapProductId = this.dataset.giftWrapId;
    this.isGiftWrappingEnabled = this.dataset.giftWrapping === "yes";
    this.cartItemCount = parseInt(this.getAttribute("cart-items-size"));
    this.giftWrapItemCount = parseInt(this.getAttribute("gift-wraps-in-cart"));
    this.totalItemCount = parseInt(this.getAttribute("items-in-cart"));
    this.giftWrapMode = this.dataset.giftWrappingLimit;
    this.isUpdatingCart = false; // Flag to prevent multiple simultaneous updates
    this.checkboxStates = new Map(); // Track checkbox states to detect user changes

    // Debounce function to prevent rapid successive updates
    this.debounceUpdate = FoxTheme.utils.debounce(
      this.handleCheckboxChange.bind(this),
      500
    );
  }

  connectedCallback() {
    this.initializeGiftWrapCheckbox();
    this.handleInitialGiftWrapState();
    this.initPerItemCheckboxListeners(); // Add per-item checkbox listeners
    // Changes v2.1: Subscribe to cart update event to sync gift wrap quantity
    document.addEventListener("cart:updated", this.handleCartUpdate.bind(this));
    // Changes v2.3: Add initial sync for cart-level checkbox
    this.syncCartLevelCheckbox();
  }

  initializeGiftWrapCheckbox() {
    const cartLevelCheckbox = this.querySelector(
      '[name="attributes[gift-wrapping]"]'
    );
    if (cartLevelCheckbox) {
      cartLevelCheckbox.addEventListener("change", (event) => {
        console.log("Cart-level checkbox changed:", event.target.checked);
        event.target.checked ? this.addGiftWrap() : this.removeGiftWrap();
      });
    }
  }

  handleInitialGiftWrapState() {
    if (this.cartItemCount == 1 && this.giftWrapItemCount > 0) {
      return this.removeGiftWrap();
    }
    if (this.giftWrapItemCount > 0 && !this.isGiftWrappingEnabled) {
      return this.addGiftWrap();
    }
    if (this.giftWrapItemCount == 0 && this.isGiftWrappingEnabled) {
      return this.addGiftWrap();
    }
  }

  initPerItemCheckboxListeners() {
    // Use event delegation to handle checkbox changes
    document.addEventListener("change", (event) => {
      const checkbox = event.target;
      if (
        checkbox.matches(
          '.cart-item-checkbox input[type="checkbox"]:not([data-gift-wrap-id])'
        )
      ) {
        const itemId = checkbox.dataset.itemId;
        const isChecked = checkbox.checked;

        // ADDED: Always process the change, rely on handleCheckboxChange for debouncing
        console.log("Per-item checkbox changed:", itemId, isChecked);
        this.checkboxStates.set(itemId, isChecked); // ADDED: Set state immediately
        this.debounceUpdate(checkbox);
      }
    });
  }

  handleCheckboxChange(checkbox) {
    if (this.isUpdatingCart || window.isCartUpdating) {
      console.log(
        "Cart update in progress, skipping checkbox change:",
        checkbox.dataset.itemId
      );
      return;
    }

    this.isUpdatingCart = true; // ADDED (already present)
    const isChecked = checkbox.checked;
    const itemId = checkbox.dataset.itemId; // ADDED (already present)
    const itemKey = checkbox.dataset.itemKey; // ADDED (already present)
    const quantity = parseInt(checkbox.dataset.quantity, 10) || 1;
    const cartLevelCheckbox = this.querySelector(
      '[name="attributes[gift-wrapping]"]'
    );
    const checkboxes = document.querySelectorAll(
      '.cart-item-checkbox input[type="checkbox"]:not([data-gift-wrap-id])'
    );

    // Changes v2.3: Disable cart-level checkbox if any per-item checkbox is checked (no action since hidden)
    if (cartLevelCheckbox) {
      cartLevelCheckbox.disabled = Array.from(checkboxes).some(
        (cb) => cb.checked
      );
    }

    // ADDED: Log itemKey, itemId, and cart_url for debugging
    console.log("Checkbox data:", {
      itemId,
      itemKey,
      isChecked,
      quantity,
      cartUrl: FoxTheme.routes.cart_url,
    });

    // ADDED: Check for data-line attribute as fallback
    const lineIndex = parseInt(checkbox.dataset.line, 10) || 0;

    // Changes v2.2: Show loader before processing line item checkbox change
    this.showLoader();

    if (lineIndex > 0) {
      // ADDED: Use data-line if available
      console.log("Using data-line attribute for line index:", lineIndex);
      return this.updatePropertiesAndGiftWrap(
        checkbox,
        isChecked,
        itemId,
        quantity,
        lineIndex
      );
    }

    // ADDED: Fetch cart state if data-line not available
    fetch(`${FoxTheme.routes.cart_url}.json`, {
      // UPDATED: Ensure .json extension
      ...FoxTheme.utils.fetchConfig("GET"),
    })
      .then((response) => {
        // ADDED: Validate response to catch redirects
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(
              `Cart fetch failed: ${response.status} ${
                response.statusText
              }, Response: ${text.substring(0, 200)}`
            );
          });
        }
        if (
          !response.headers.get("content-type")?.includes("application/json")
        ) {
          return response.text().then((text) => {
            throw new Error(
              `Expected JSON, got ${response.headers.get(
                "content-type"
              )}: ${text.substring(0, 200)}`
            );
          });
        }
        return response.json();
      })
      .then((cart) => {
        // ADDED: Find line index (1-based) for the item
        const lineIndex =
          cart.items.findIndex((item) => item.variant_id == itemId) + 1;

        if (lineIndex === 0) {
          console.error("Item not found in cart:", itemId);
          this.isUpdatingCart = false;
          return;
        }

        return this.updatePropertiesAndGiftWrap(
          checkbox,
          isChecked,
          itemId,
          quantity,
          lineIndex
        );
      })
      .catch((error) => {
        console.error("Error fetching cart or updating properties:", error);
        this.isUpdatingCart = false;
        alert("Failed to update gift wrap. Please try again."); // CHANGED
      });
  }

  // ADDED: Helper method to update properties and gift wrap
  updatePropertiesAndGiftWrap(
    checkbox,
    isChecked,
    itemId,
    quantity,
    lineIndex
  ) {
    fetch(`${FoxTheme.routes.cart_change_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      body: JSON.stringify({
        line: lineIndex,
        quantity,
        properties: { "Should be wrapped": isChecked ? "Yes" : "" },
      }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((error) => {
            throw new Error(JSON.stringify(error));
          });
        }
        return response.json();
      })
      .then((parsedState) => {
        console.log(
          "Cart response items:",
          parsedState.items.map((item) => ({
            key: item.key,
            variant_id: item.variant_id,
            properties: item.properties,
          }))
        );
        // ADDED: Update checkboxStates to reflect API response
        this.checkboxStates.set(itemId, isChecked);

        // ADDED: Reset isUpdatingCart before gift wrap product updates
        const giftWrapQuantity = this.getGiftWrapQuantity(); // CHANGED
        const cartLevelChecked = this.querySelector(
          '[name="attributes[gift-wrapping]"]'
        )?.checked; // CHANGED
        this.isUpdatingCart = false;

        if (giftWrapQuantity > 0) {
          // Changes v2.3: Update UI and sync checkbox state
          // Changes v2.4: Update Gift Wrap quantity based on checked items' total quantities
          console.log("Updating Gift Wrap quantity:", giftWrapQuantity); // CHANGED
          this.addGiftWrap(); // CHANGED
        } else if (this.giftWrapItemCount > 0) {
          // Changes v2.3: Remove Gift Wrap if no items checked
          // Changes v2.4: Remove Gift Wrap if no items checked or total quantity is 0
          console.log("Removing Gift Wrap, no items checked or quantity 0");
          this.removeGiftWrap(); // CHANGED
        } else {
          // Changes v2.3: Update UI and sync checkbox state
          this.updateGiftNoteVisibility(); // CHANGED
          this.syncCartLevelCheckbox(); // Changes v2.3
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            // CHANGED
            cart: parsedState, // CHANGED
            checkbox, // CHANGED
            isChecked, // CHANGED
          }); // CHANGED
        }
      })
      .catch((error) => {
        console.error("Error updating item properties:", error);
        this.isUpdatingCart = false;
        alert("Failed to update gift wrap. Please try again."); // CHANGED
      });
  }

  // Changes v2.1: Handle cart update event to sync gift wrap quantity
  handleCartUpdate(event) {
    if (this.isUpdatingCart || window.isCartUpdating) return;

    this.isUpdatingCart = true;
    const cart = event.detail.cart;
    const currentGiftWrapQuantity = this.giftWrapItemCount;
    const newGiftWrapQuantity = this.getGiftWrapQuantity();

    if (currentGiftWrapQuantity !== newGiftWrapQuantity) {
      console.log("Gift Wrap quantity mismatch detected:", {
        current: currentGiftWrapQuantity,
        new: newGiftWrapQuantity,
      });
      this.showLoader();
      const sectionsToUpdate = this.getSectionsToUpdate();
      const requestBody = this.createRequestBody(
        { [this.giftWrapProductId]: newGiftWrapQuantity },
        { "gift-wrapping": newGiftWrapQuantity > 0 ? "yes" : "" },
        sectionsToUpdate
      );
      this.updateCart(requestBody);
    }
    this.isUpdatingCart = false;
    // Changes v2.3: Sync cart-level checkbox after cart update
    this.syncCartLevelCheckbox();
  }

  // Changes v2.3: New method to sync cart-level checkbox with line item states
  syncCartLevelCheckbox() {
    const cartLevelCheckbox = this.querySelector(
      '[name="attributes[gift-wrapping]"]'
    );
    if (cartLevelCheckbox) {
      const checkboxes = document.querySelectorAll(
        '.cart-item-checkbox input[type="checkbox"]:not([data-gift-wrap-id])'
      );
      const allUnchecked = Array.from(checkboxes).every((cb) => !cb.checked);
      if (allUnchecked) {
        cartLevelCheckbox.checked = false;
        this.isGiftWrappingEnabled = false;
        console.log(
          "All line items unchecked, cart-level checkbox set to false"
        );
      } else {
        console.log(
          "Some line items checked, cart-level checkbox remains unchanged"
        );
      }
    }
  }

  // Changes v2.4: Update to calculate gift wrap quantity based on total product quantities
  getGiftWrapQuantity() {
    // CHANGED
    const checkboxes = document.querySelectorAll(
      '.cart-item-checkbox input[type="checkbox"]:not([data-gift-wrap-id])'
    ); // CHANGED

    let totalQuantity = 0;
    checkboxes.forEach((cb) => {
      if (cb.checked) {
        const quantity = parseInt(cb.dataset.quantity, 10) || 1;
        totalQuantity += quantity;
      }
    });
    return totalQuantity;
  } // CHANGED

  addGiftWrap() {
    if (this.isUpdatingCart || window.isCartUpdating) {
      console.log("Cart update in progress, skipping addGiftWrap");
      return;
    }
    const giftWrapQuantity = this.getGiftWrapQuantity(); // CHANGED
    // Changes v2.3: Skip addGiftWrap if no items checked
    if (giftWrapQuantity === 0) {
      // CHANGED
      console.log("No items checked for gift wrapping, skipping addGiftWrap"); // CHANGED
      this.isUpdatingCart = false; // CHANGED
      return; // CHANGED
    }

    this.isUpdatingCart = true;
    this.showLoader();
    const sectionsToUpdate = this.getSectionsToUpdate();
    const requestBody = this.createRequestBody(
      {
        [this.giftWrapProductId]: giftWrapQuantity || 1, // CHANGED
      },
      { "gift-wrapping": "yes" }, // ADDED: Set gift-wrapping attribute
      sectionsToUpdate
    );
    console.log("Adding Gift Wrap with attributes:", requestBody); // ADDED: Debug log
    this.updateCart(requestBody);
  }

  removeGiftWrap() {
    if (this.isUpdatingCart || window.isCartUpdating) {
      console.log("Cart update in progress, skipping removeGiftWrap");
      return;
    }

    const giftWrapQuantity = this.getGiftWrapQuantity(); // CHANGED
    // Changes v2.3: Removed cartLevelChecked check since cart-level checkbox is hidden
    // Original: const cartLevelChecked = this.querySelector('[name="attributes[gift-wrapping]"]')?.checked;
    if (giftWrapQuantity > 0) {
      // Changes v2.3: Update gift wrap quantity if items are checked
      console.log("Items still checked, updating gift wrap quantity"); // CHANGED
      this.isUpdatingCart = true; // CHANGED
      this.showLoader(); // CHANGED
      const sectionsToUpdate = this.getSectionsToUpdate(); // CHANGED
      const requestBody = this.createRequestBody(
        // CHANGED
        {
          // CHANGED
          [this.giftWrapProductId]: giftWrapQuantity, // CHANGED
        }, // CHANGED
        { "gift-wrapping": "yes" }, // Maintain if items are checked
        sectionsToUpdate // CHANGED
      ); // CHANGED
      this.updateCart(requestBody); // CHANGED
      return; // CHANGED
    }

    this.isUpdatingCart = true;
    this.showLoader();
    const sectionsToUpdate = this.getSectionsToUpdate();
    const newGiftWrapQuantity = this.getGiftWrapQuantity(); // Changes v2.1: Recalculate gift wrap quantity after item removal
    const requestBody = this.createRequestBody(
      {
        [this.giftWrapProductId]: newGiftWrapQuantity, // Changes v2.1: Set Gift Wrap quantity based on remaining checked items
      },
      { "gift-wrapping": "", "gift-note": "" },
      sectionsToUpdate
    );
    this.updateCart(requestBody);
  }

  getSectionsToUpdate() {
    let sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:grouped-sections", {
        bubbles: true,
        detail: { sections },
      })
    );
    return sections;
  }

  createRequestBody(updates, attributes, sections) {
    return JSON.stringify({ updates, attributes, sections });
  }

  updateCart(body, checkbox, isChecked) {
    window.isCartUpdating = true; // Set flag to prevent concurrent updates
    fetch(`${FoxTheme.routes.cart_update_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      ...{ body },
    })
      .then((response) => response.json())
      .then((parsedState) => {
        console.log(
          "Cart updated:",
          parsedState.items.map((item) => ({
            variant_id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties,
          })),
          "Attributes:",
          parsedState.attributes // ADDED: Log attributes
        );
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: parsedState,
          checkbox, // ADDED
          isChecked, // ADDED
        });

        // Update local state
        this.isGiftWrappingEnabled =
          parsedState.attributes["gift-wrapping"] === "yes";
        console.log(
          "isGiftWrappingEnabled set to:",
          this.isGiftWrappingEnabled
        ); // ADDED: Debug log

        // Changes v2.3: Update cart-level checkbox state to sync with line items
        const cartLevelCheckbox = document.querySelector(
          '[name="attributes[gift-wrapping]"]'
        );
        if (cartLevelCheckbox) {
          cartLevelCheckbox.checked = this.isGiftWrappingEnabled;
          console.log(
            "Updated cart-level checkbox state:",
            this.isGiftWrappingEnabled
          );
        }

        // Update checkbox states after re-render
        if (checkbox && typeof isChecked === "boolean") {
          const currentCheckbox = document.querySelector(
            `.cart-item-checkbox input[type="checkbox"][data-item-id="${checkbox.dataset.itemId}"]:not([data-gift-wrap-id])`
          );
          if (currentCheckbox) {
            currentCheckbox.checked = isChecked;
            console.log(
              "Updated checkbox state post-re-render:",
              checkbox.dataset.itemId,
              isChecked
            );
          }
        }

        // Update other checkbox states after re-render
        const checkboxes = document.querySelectorAll(
          '.cart-item-checkbox input[type="checkbox"]:not([data-gift-wrap-id])'
        );
        checkboxes.forEach((cb) => {
          if (cb !== checkbox) {
            const itemId = cb.dataset.itemId;
            const isChecked = cb.checked;
            this.checkboxStates.set(itemId, isChecked);
            console.log("Updated other checkbox state:", itemId, isChecked);
          }
        });

        this.cartItemCount = parsedState.item_count;
        this.giftWrapItemCount =
          parsedState.items.find(
            (item) => item.variant_id == this.giftWrapProductId
          )?.quantity || 0;
        this.totalItemCount = parsedState.item_count - this.giftWrapItemCount;

        this.updateGiftNoteVisibility();
        this.isUpdatingCart = false; // Reset flag after update
        window.isCartUpdating = false; // Reset global flag
        console.log("Resetting isUpdatingCart to false");
        // Changes v2.3: Sync cart-level checkbox after update
        this.syncCartLevelCheckbox();
      })
      .catch((error) => {
        console.error("Error updating cart:", error);
        this.isUpdatingCart = false; // Reset flag on error
        window.isCartUpdating = false; // Reset global flag
        alert("Failed to update gift wrap. Please try again."); // CHANGED
      });
  }

  updateGiftNoteVisibility() {
    const giftNote = this.querySelector(
      'textarea[name="attributes[gift-note]"]'
    );
    if (giftNote) {
      giftNote.parentElement.style.display =
        this.giftWrapItemCount > 0 || this.isGiftWrappingEnabled
          ? "block"
          : "none";
      console.log(
        "Gift note visibility updated:",
        giftNote.parentElement.style.display
      );
    }
  }

  showLoader() {
    const loaderElement = this.querySelector(".loader");
    if (loaderElement) loaderElement.classList.add("btn--loading");
  }
}
customElements.define("gift-wrapping", GiftWrapping);

class GiftNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "change",
      FoxTheme.utils.debounce(this.updateGiftNote.bind(this), 500)
    );
  }

  updateGiftNote(event) {
    if (window.isCartUpdating) {
      console.log("Cart update in progress, skipping gift note update");
      return;
    }
    const requestBody = JSON.stringify({
      attributes: { "gift-note": event.target.value },
    });
    fetch(`${FoxTheme.routes.cart_update_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      ...{ body: requestBody },
    });
  }
}
customElements.define("gift-note", GiftNote);

class RemoveGiftWrapButton extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();

      const cartItemsElement = this.closest("cart-items");
      cartItemsElement.showLoader(this.dataset.index);

      const giftWrappingElement = document.querySelector("gift-wrapping");
      // Changes v2.1: Pass the index of the removed item to adjust gift wrap quantity
      giftWrappingElement.removeGiftWrap(this.dataset.index);
    });
  }
}
customElements.define("gift-wrap-remove-item", RemoveGiftWrapButton, {
  extends: "a",
});
